import { useState } from 'react'
import { Trash2 } from 'lucide-react'

import { DEAL_STAGES, STAGE_LABELS, type Deal, type DealStage } from '@/types/deal'
import { useAssignableSalesReps, useCustomers } from '@/features/customers/hooks'
import { customerDisplayName } from '@/features/customers/customer-utils'
import { boatOptionLabel } from '@/features/consignment/consignment-form'
import { useBoats } from '@/features/inventory/hooks'
import { useMe } from '@/features/auth/hooks'
import { useDirtyClose } from '@/hooks/use-dirty-close'
import { useCreateDeal, useDeleteDeal, useUpdateDeal } from '@/features/customers/deal-hooks'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PriceInput } from '@/components/ui/price-input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface FormState {
  title: string
  customer_id: string
  boat_id: string // "none" = no boat link
  base_value: string
  stage: DealStage
  owner: string
  expected_close: string
}

interface DealEditDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'create' | 'edit'
  deal?: Deal | null
}

export function DealEditDialog({ open, onOpenChange, mode, deal }: DealEditDialogProps) {
  const { data: customers = [], isLoading: customersLoading } = useCustomers()
  const { data: boats = [] } = useBoats()
  const { data: me } = useMe()
  const { data: salesReps = [] } = useAssignableSalesReps()
  const createMutation = useCreateDeal()
  const updateMutation = useUpdateDeal()
  const deleteMutation = useDeleteDeal()

  const [form, setForm] = useState<FormState>({
    title: '',
    customer_id: '',
    boat_id: 'none',
    base_value: '',
    stage: 'prospecting',
    owner: '',
    expected_close: '',
  })
  const [deleteOpen, setDeleteOpen] = useState(false)
  // Snapshot of the seeded values — anything else means unsaved edits.
  const [seed, setSeed] = useState<FormState | null>(null)

  // Reset the form whenever the dialog opens or the target deal changes.
  // Implemented as "adjust state during render" (see React docs) to avoid a
  // setState-in-effect cascading render.
  const formKey = `${open}:${mode}:${deal?.id ?? ''}`
  const [prevFormKey, setPrevFormKey] = useState(formKey)
  if (prevFormKey !== formKey) {
    setPrevFormKey(formKey)
    if (open && mode === 'edit' && deal) {
      const seeded: FormState = {
        title: deal.title,
        customer_id: deal.customer_id,
        boat_id: deal.boat_id ?? 'none',
        base_value: String(deal.base_value),
        stage: deal.stage,
        owner: deal.owner,
        expected_close: deal.expected_close ?? '',
      }
      setForm(seeded)
      setSeed(seeded)
    } else if (open) {
      const seeded: FormState = {
        title: '',
        customer_id: '',
        boat_id: 'none',
        base_value: '',
        stage: 'prospecting',
        // New deals default to the signed-in rep; cleared to a pick below if
        // they are not in the directory.
        owner: me?.display_name ?? '',
        expected_close: '',
      }
      setForm(seeded)
      setSeed(seeded)
    }
  }

  // Rep options: the employee directory, plus whoever is currently set
  // (e.g. a legacy free-text owner) so edits never silently reassign.
  const repOptions = Array.from(new Set([...salesReps, ...(form.owner ? [form.owner] : [])]))

  const isDirty = seed !== null && JSON.stringify(form) !== JSON.stringify(seed)
  const { guardOpenChange, discardGuard } = useDirtyClose(open, onOpenChange, isDirty)

  const hasCustomers = customers.length > 0
  const isValid = form.title.trim().length > 0 && form.customer_id.length > 0

  const handleSubmit = async () => {
    if (!isValid) return
    const body = {
      title: form.title.trim(),
      customer_id: form.customer_id,
      boat_id: form.boat_id === 'none' ? undefined : form.boat_id,
      base_value: Number(form.base_value.replace(/,/g, '')) || 0,
      stage: form.stage,
      owner: form.owner.trim() || me?.display_name || 'Unassigned',
      expected_close: form.expected_close || undefined,
    }

    try {
      if (mode === 'create') {
        await createMutation.mutateAsync(body)
      } else if (deal) {
        await updateMutation.mutateAsync({ id: deal.id, body })
      }
      onOpenChange(false)
    } catch {
      // error surfaces via the global mutation toast; keep dialog open
    }
  }

  const handleDelete = async () => {
    if (!deal) return
    try {
      await deleteMutation.mutateAsync(deal.id)
      setDeleteOpen(false)
      onOpenChange(false)
    } catch {
      // error surfaces via the global mutation toast
    }
  }

  const pending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  return (
    <Dialog open={open} onOpenChange={guardOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'New Deal' : 'Edit Deal'}</DialogTitle>
          <DialogDescription>
            {mode === 'create' ? 'Add a new deal to your pipeline.' : 'Update this deal.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Title</label>
            <Input
              placeholder="Annual slip lease"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Customer</label>
            {hasCustomers ? (
              <Select
                value={form.customer_id}
                onValueChange={(v) => setForm((f) => ({ ...f, customer_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={customersLoading ? 'Loading…' : 'Select customer'} />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {customerDisplayName(c)}
                      {c.company_name ? ` (${c.company_name})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">
                No customers yet — create a customer first.
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Boat (optional)</label>
            <Select
              value={form.boat_id}
              onValueChange={(v) =>
                setForm((f) => {
                  // Picking a boat with no base price yet (empty or 0)
                  // prefills it from the listing — the server seeds the same
                  // default when the base is left empty on a new link.
                  const boat = boats.find((b) => b.id === v)
                  const baseEmpty = f.base_value.trim() === '' || f.base_value.trim() === '0'
                  const base = baseEmpty && boat ? String(boat.price) : f.base_value
                  return { ...f, boat_id: v, base_value: base }
                })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="No boat" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No boat</SelectItem>
                {boats.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {boatOptionLabel(b)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Base Price (USD)</label>
              <PriceInput
                placeholder="25,000"
                value={form.base_value}
                onChange={(v) => setForm((f) => ({ ...f, base_value: v }))}
                onBlur={() => {}}
              />
              {form.boat_id !== 'none' && form.base_value.trim() === '' ? (
                <p className="text-xs text-muted-foreground">
                  Leave empty to price this deal from the boat's listing.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Add-ons are itemized on the deal and added on top of this price.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Stage</label>
              <Select
                value={form.stage}
                onValueChange={(v) => setForm((f) => ({ ...f, stage: v as DealStage }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  {DEAL_STAGES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {STAGE_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Sales Rep</label>
              <Select
                value={form.owner}
                onValueChange={(v) => setForm((f) => ({ ...f, owner: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a sales rep" />
                </SelectTrigger>
                <SelectContent>
                  {repOptions.map((rep) => (
                    <SelectItem key={rep} value={rep}>
                      {rep}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Expected close</label>
              <Input
                type="date"
                value={form.expected_close}
                onChange={(e) => setForm((f) => ({ ...f, expected_close: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {mode === 'edit' && (
            <Button
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
              disabled={pending}
              className="mr-auto"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={pending || !isValid || !hasCustomers}>
            {mode === 'create' ? 'Create Deal' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Delete confirmation — deleting removes the deal and its history. */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete deal?</DialogTitle>
            <DialogDescription>
              “{deal?.title}” is permanently removed from the pipeline. This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete Deal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {discardGuard}
    </Dialog>
  )
}
